// src/modals/BudgetDetailsModal.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import BaseExpandedCard from '../components/cards/BaseExpandedCard';
import { Logger } from '../utils/logger';

const logger = new Logger('BudgetDetailsModal');

/**
 * BudgetDetailsModal displays detailed budget information for a project,
 * including breakdown by category, monthly spending, and variance analysis.
 */
const BudgetDetailsModal = ({ 
  open, 
  onClose, 
  projectName, 
  projectId 
}) => {
  /*
  // Original implementation commented out
  const [budgetData, setBudgetData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Simulated data for placeholder purposes
  const placeholderData = {
    total_budget: parseFloat(budget?.replace(/[^0-9.-]+/g, '') || 0),
    used_amount: Math.round((budgetUsedPercentage / 100) * parseFloat(budget?.replace(/[^0-9.-]+/g, '') || 0)),
    remaining_amount: Math.round((1 - budgetUsedPercentage / 100) * parseFloat(budget?.replace(/[^0-9.-]+/g, '') || 0)),
    is_over_budget: isOverBudget,
    budget_variance_percentage: isOverBudget ? 5 : -10,
    monthly_spending: [
      { month: 'Jan', amount: 20000 },
      { month: 'Feb', amount: 35000 },
      { month: 'Mar', amount: 25000 },
      { month: 'Apr', amount: 30000 }
    ],
    category_breakdown: [
      { category: 'Development', amount: 50000, percentage: 40 },
      { category: 'Design', amount: 20000, percentage: 16 },
      { category: 'Project Management', amount: 30000, percentage: 24 },
      { category: 'QA', amount: 15000, percentage: 12 },
      { category: 'DevOps', amount: 10000, percentage: 8 }
    ],
    last_updated: new Date()
  };

  // Fetch budget data when the modal opens
  useEffect(() => {
    if (open && projectId) {
      setIsLoading(true);
      
      // In a real implementation, this would be an API call
      // For now, we'll simulate an API call with a timeout
      const timer = setTimeout(() => {
        logger.debug(`Fetching budget data for project: ${projectId}`);
        setBudgetData(placeholderData);
        setIsLoading(false);
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [open, projectId, budget, budgetUsedPercentage, isOverBudget]);

  // Format currency with $ sign and commas
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Show loading state
  if (isLoading) {
    return (
      <BaseExpandedCard
        open={open}
        onClose={onClose}
        title={`Budget Details: ${projectName}`}
        subtitle="Loading data..."
      >
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '300px' 
        }}>
          <CircularProgress />
        </Box>
      </BaseExpandedCard>
    );
  }

  // Show error state
  if (error) {
    return (
      <BaseExpandedCard
        open={open}
        onClose={onClose}
        title={`Budget Details: ${projectName}`}
        subtitle="Error loading data"
      >
        <Box sx={{ 
          p: 4, 
          textAlign: 'center', 
          color: 'error.main',
          bgcolor: 'background.paper',
          borderRadius: 1
        }}>
          <Typography variant="h6">Error</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {error}
          </Typography>
        </Box>
      </BaseExpandedCard>
    );
  }

  // Use the data (either from API or placeholder)
  const data = budgetData || placeholderData;

  return (
    <BaseExpandedCard
      open={open}
      onClose={onClose}
      title={`Budget Details: ${projectName}`}
      subtitle={`Project ID: ${projectId} | Total Budget: ${formatCurrency(data.total_budget)}`}
    >
      // Summary cards
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card sx={{ bgcolor: 'background.paper', height: '100%' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Used Amount
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography 
                    variant="h3" 
                    sx={{ 
                      fontWeight: 'bold',
                      color: '#BB86FC'
                    }}
                  >
                    {formatCurrency(data.used_amount)}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {budgetUsedPercentage}% of total budget
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={Math.min(budgetUsedPercentage, 100)} 
                  sx={{ 
                    height: 10,
                    borderRadius: 5,
                    bgcolor: '#111',
                    mt: 1,
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 5,
                      bgcolor: budgetUsedPercentage > 95 ? '#CF6679' : '#BB86FC'
                    }
                  }}
                />
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card sx={{ bgcolor: 'background.paper', height: '100%' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Remaining Budget
                </Typography>
                <Typography 
                  variant="h3" 
                  sx={{ 
                    fontWeight: 'bold',
                    color: data.is_over_budget ? '#CF6679' : '#03DAC6'
                  }}
                >
                  {formatCurrency(data.remaining_amount)}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    mt: 1,
                    color: data.is_over_budget ? '#CF6679' : 'text.secondary'
                  }}
                >
                  {data.is_over_budget ? 'Over budget!' : `${100 - budgetUsedPercentage}% remaining`}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card sx={{ bgcolor: 'background.paper', height: '100%' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Budget Variance
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography 
                    variant="h3" 
                    sx={{ 
                      fontWeight: 'bold',
                      color: data.budget_variance_percentage > 0 ? '#CF6679' : '#03DAC6'
                    }}
                  >
                    {data.budget_variance_percentage > 0 ? '+' : ''}
                    {data.budget_variance_percentage}%
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {data.budget_variance_percentage > 0 
                    ? 'Over projected budget' 
                    : 'Under projected budget'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
      
      // Category breakdown
      <Typography variant="h6" sx={{ mb: 2 }}>
        Budget Breakdown by Category
      </Typography>
      
      <TableContainer component={Paper} sx={{ bgcolor: 'background.paper', mb: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Category</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Percentage</TableCell>
              <TableCell>Visualization</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.category_breakdown.map((category, index) => (
              <TableRow key={index}>
                <TableCell>
                  {category.category}
                </TableCell>
                <TableCell>
                  {formatCurrency(category.amount)}
                </TableCell>
                <TableCell>
                  {category.percentage}%
                </TableCell>
                <TableCell>
                  <Box sx={{ width: '100%', display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ width: '100%', mr: 1 }}>
                      <LinearProgress 
                        variant="determinate" 
                        value={category.percentage} 
                        sx={{ 
                          height: 8,
                          borderRadius: 5,
                          bgcolor: '#111',
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 5,
                            bgcolor: '#64B5F6'
                          }
                        }}
                      />
                    </Box>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      
      // Monthly spending
      <Typography variant="h6" sx={{ mb: 2 }}>
        Monthly Spending
      </Typography>
      
      <TableContainer component={Paper} sx={{ bgcolor: 'background.paper', mb: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Month</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Percentage of Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.monthly_spending.map((month, index) => {
              const percentage = Math.round((month.amount / data.total_budget) * 100);
              
              return (
                <TableRow key={index}>
                  <TableCell>
                    {month.month}
                  </TableCell>
                  <TableCell>
                    {formatCurrency(month.amount)}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box sx={{ width: 100, mr: 1 }}>
                        <LinearProgress 
                          variant="determinate" 
                          value={percentage} 
                          sx={{ 
                            height: 8,
                            borderRadius: 5,
                            bgcolor: '#111',
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 5,
                              bgcolor: '#FFCF44'
                            }
                          }}
                        />
                      </Box>
                      <Typography variant="body2">
                        {percentage}%
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      
      <Box sx={{ mt: 3 }}>
        <Divider sx={{ mb: 2 }} />
        <Typography variant="caption" color="text.secondary">
          Budget data last updated: {data.last_updated.toLocaleString()}
        </Typography>
      </Box>
    </BaseExpandedCard>
  );
  */

  return (
    <BaseExpandedCard
      open={open}
      onClose={onClose}
      title={`Budget Details: ${projectName}`}
    >
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '300px',
        textAlign: 'center'
      }}>
        <Typography variant="h4">
          Place Holder for Budget Details
        </Typography>
      </Box>
    </BaseExpandedCard>
  );
};

BudgetDetailsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  projectName: PropTypes.string.isRequired,
  projectId: PropTypes.string.isRequired,
  budget: PropTypes.string,
  budgetUsedPercentage: PropTypes.number,
  isOverBudget: PropTypes.bool
};

export default BudgetDetailsModal;
